import React, { useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import Icon from 'components/UI/Icon';
import { createTabID, createPanelID } from 'utils/tabs';
import { useHorizontalScroll, useWindowResize } from 'utils/hooks';
import { useLang, useTrans } from 'utils/localisation';
import { pushCustomEvent } from 'utils/analytics';
import { ALL_GALLERIES_KEY } from '../../../constants';

import * as styles from './GalleryFilters.module.scss';

function GalleryFilters({
  galleries,
  active,
  setActive,
  className,
  tabsAriaLabel = 'Gallery Filters',
}) {
  const t = useTrans(['filters']);
  const filters = [
    { id: ALL_GALLERIES_KEY, title: t('allClientStories'), icon: 'all' },
    ...galleries.map(({ id, title, icon }) => ({ id, title, icon })),
  ];
  const lang = useLang();
  const tabRef = useRef([]);
  const { scrollRef, scrollLeft, setScrollLeft } = useHorizontalScroll();
  const { width } = useWindowResize();
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const scrollContainer = scrollRef?.current;
  const isDesktop = width > 1024;
  const showScrollButtons =
    isDesktop && scrollContainer?.clientWidth !== scrollContainer?.scrollWidth;
  const cardWidth = 198;
  const cardsGap = isDesktop ? 30 : 20;
  const distanceToScroll = cardWidth + cardsGap;

  const checkForScrollPosition = () => {
    if (scrollContainer) {
      const { scrollWidth, clientWidth } = scrollContainer;

      if (lang === 'ae') {
        setCanScrollLeft(-scrollLeft < scrollWidth - clientWidth);
        setCanScrollRight(scrollLeft < 0);
      } else {
        setCanScrollLeft(scrollLeft > 0);
        setCanScrollRight(scrollLeft < scrollWidth - clientWidth);
      }
    }
  };

  const scrollTo = newScrollPosition => {
    setScrollLeft(newScrollPosition);
    scrollContainer.scrollTo({
      left: newScrollPosition,
      behavior: 'smooth',
    });
  };

  const scrollToVisible = () => {
    if (scrollContainer) {
      const tabRightPosition =
        tabRef.current[active].offsetLeft + tabRef.current[active].clientWidth;
      const containerRightPosition = scrollContainer.scrollLeft + scrollContainer.clientWidth;

      if (tabRightPosition > containerRightPosition) {
        const newScrollPosition =
          scrollContainer.scrollLeft + (tabRightPosition - containerRightPosition);
        scrollTo(newScrollPosition);
      }
      if (scrollContainer.scrollLeft > tabRef.current[active].offsetLeft) {
        const newScrollPosition =
          scrollContainer.scrollLeft +
          (tabRef.current[active].offsetLeft - scrollContainer.scrollLeft);
        scrollTo(newScrollPosition);
      }
    }
  };

  const handleScroll = direction => {
    if (scrollContainer) {
      const newScrollPosition =
        scrollContainer.scrollLeft + (direction === 'left' ? -distanceToScroll : distanceToScroll);

      scrollTo(newScrollPosition);
    }
  };

  useEffect(() => {
    if (showScrollButtons) {
      checkForScrollPosition();
    }
  }, [scrollLeft, scrollContainer, showScrollButtons]);

  useEffect(() => {
    setTimeout(scrollToVisible, 200);
  }, [active]);

  const handleChange = item => () => {
    setActive(item);
    pushCustomEvent('ndrsn_projects_galleries_filters', {
      action: 'click_on_card',
      gallery: item,
    });
  };

  return (
    <div className={styles.wrapper}>
      <button
        onClick={() => handleScroll('left')}
        className={styles.scrollButtonLeft}
        disabled={!canScrollLeft}
        hidden={!showScrollButtons}
        aria-label="Scroll left"
      />
      <div
        className={cn(styles.buttonRow, className)}
        ref={scrollRef}
        role="tablist"
        aria-label={tabsAriaLabel}
      >
        {filters.map(({ id, title, icon }, i) => {
          const isCurrentTab = active === id;

          return (
            <button
              key={id}
              ref={el => (tabRef.current[id] = el)}
              className={cn(styles.button, isCurrentTab && styles.active)}
              onClick={handleChange(id)}
              role="tab"
              aria-selected={isCurrentTab}
              aria-controls={createPanelID(tabsAriaLabel, i)}
              id={createTabID(tabsAriaLabel, i)}
              tabIndex={isCurrentTab ? 0 : -1}
              disabled={isCurrentTab}
            >
              {icon && (
                <div className={styles.logoWrapper}>
                  <Icon className={styles.logo} name={icon} />
                </div>
              )}
              {title && <span>{title}</span>}
            </button>
          );
        })}
      </div>
      <button
        onClick={() => handleScroll('right')}
        className={styles.scrollButtonRight}
        disabled={!canScrollRight}
        hidden={!showScrollButtons}
        aria-label="Scroll Right"
      />
    </div>
  );
}

export default React.memo(GalleryFilters);
